import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  VStack,
  HStack,
  Input,
  Textarea,
  Button,
  FormControl,
  FormLabel,
  Switch,
  Text,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Progress,
  Badge,
  Select,
  NumberInput,
  IconButton,
  Divider,
  useColorMode,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon, EditIcon } from '@chakra-ui/icons';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { CreateCategoryRequest, CreateQuestionRequest } from '../../types/game.ts';
import { GTMEvents, sendGTMEvent } from '../../utils/gtm.ts';
import './CategoryComponents.css';

const MotionBox = motion(Box);

interface CategoryCreatorProps {
  isOpen: boolean;
  onClose: () => void;
  onCategoryCreated?: () => void;
  user?: any; // Utilisateur Firebase
  editCategory?: any; // Catégorie à éditer (optionnel)
}

interface QuestionForm extends CreateQuestionRequest {
  id: string; // ID temporaire pour React
}

const CategoryCreator: React.FC<CategoryCreatorProps> = ({
  isOpen,
  onClose,
  onCategoryCreated,
  user,
  editCategory
}) => {
  const { t } = useTranslation();
  const { colorMode } = useColorMode();
  const toast = useToast();

  // État du formulaire de catégorie
  const [categoryName, setCategoryName] = useState('');
  const [categoryDescription, setCategoryDescription] = useState('');
  const [categoryColor, setCategoryColor] = useState('#6B73FF');
  const [categoryEmoji, setCategoryEmoji] = useState('🎯');
  const [isPublic, setIsPublic] = useState(true);
  const [questions, setQuestions] = useState<QuestionForm[]>([]);
  const [currentLanguage, setCurrentLanguage] = useState('fr');
  
  // État de l'interface
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(1); // 1: Catégorie, 2: Questions
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(null);
  const [importMode, setImportMode] = useState<'replace' | 'add'>('add'); // Mode d'import
  const [availableEmojis, setAvailableEmojis] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState(''); // État pour la recherche

  // Ref pour le container de scroll
  const modalBodyRef = useRef<HTMLDivElement>(null);

  // Couleurs prédéfinies pour les catégories
  const predefinedColors = [
    '#6B73FF', '#FF6B6B', '#4ECDC4', '#45B7D1',
    '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8',
    '#F7DC6F', '#BB8FCE', '#85C1E9', '#F8C471'
  ];

  // Emojis prédéfinis pour les catégories
  const baseEmojis = [
    '🎯', '🎮', '🏆', '🎨', '🔬', '📚', '🎭', '🎪',
    '🎵', '⚽', '🏀', '🎳', '🎲', '🃏', '🎸', '🎤',
    '🍕', '🌟', '💎', '🔥', '⚡', '🌈', '🦄', '🐱',
    '🚀', '💡', '🎊', '🎈', '🏅', '👑', '🎁', '✨'
  ];

  const { i18n } = useTranslation();
  const API_URL = process.env.REACT_APP_API_URL || 'https://api.quizvs.com';

  // Normalise une difficulté éventuelle 1..10 vers notre échelle 1..5
  const normalizeDifficulty5 = (val: number | undefined | null): number => {
    const n = Number(val);
    if (!Number.isFinite(n)) return 3;
    if (n <= 5) return Math.max(1, Math.min(5, Math.round(n)));
    return Math.max(1, Math.min(5, Math.ceil(n / 2))); // ex: 6->3, 7->4, 9->5, 10->5
  };

  // Fonction de filtrage des questions basée sur la recherche
  const filteredQuestions = React.useMemo(() => {
    if (!searchQuery.trim()) return questions;
    
    const query = searchQuery.toLowerCase().trim();
    return questions.filter((question) => {
      // Recherche dans le texte de la question
      if (question.question_text.toLowerCase().includes(query)) return true;
      
      // Recherche dans toutes les options de réponse
      if (question.option_1.toLowerCase().includes(query)) return true;
      if (question.option_2.toLowerCase().includes(query)) return true;
      if (question.option_3.toLowerCase().includes(query)) return true;
      if (question.option_4.toLowerCase().includes(query)) return true;
      
      return false;
    });
  }, [questions, searchQuery]);

  // Fonction pour surligner les mots recherchés
  const highlightText = (text: string, query: string) => {
    if (!query.trim()) return text;
    
    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return parts.map((part, i) => 
      part.toLowerCase() === query.toLowerCase() ? 
        <Text as="span" key={i} bg="yellow.200" _dark={{ bg: "yellow.700" }} px={1} borderRadius="sm" fontWeight="bold">
          {part}
        </Text> : part
    );
  };

  useEffect(() => {
    if (isOpen) {
      if (editCategory) {
        // Mode édition : charger les données depuis l'API
        loadCategoryForEdit();
      } else {
        // Mode création : réinitialiser le formulaire
        resetForm();
      }
      setCurrentLanguage(i18n.language.split('-')[0]);
      // Initialiser les emojis disponibles avec les emojis de base
      setAvailableEmojis([...baseEmojis]);
    }
  }, [isOpen, editCategory, i18n.language]);

  // Effet pour forcer la gestion des événements de scroll de la souris
  useEffect(() => {
    if (isOpen && modalBodyRef.current) {
      const modalBody = modalBodyRef.current;
      
      // Fonction pour gérer le scroll uniquement sur desktop
      const handleWheel = (e: WheelEvent) => {
        // Vérifier si c'est un appareil tactile
        const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
        
        // Ne pas gérer le wheel sur les appareils tactiles
        if (isTouchDevice) return;
        
        // Empêcher la propagation vers le parent seulement sur desktop
        e.stopPropagation();
        
        // Scroll manuel si nécessaire
        if (modalBody) {
          modalBody.scrollTop += e.deltaY;
        }
      };

      // Fonction pour améliorer le scroll tactile
      const handleTouchMove = (e: TouchEvent) => {
        // Permettre le comportement de scroll natif
        e.stopPropagation();
      };

      // Ajouter l'écouteur d'événement wheel seulement sur desktop
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      if (!isTouchDevice) {
        modalBody.addEventListener('wheel', handleWheel, { passive: false });
        modalBody.focus();
      } else {
        // Pour les appareils tactiles, optimiser le scroll
        modalBody.addEventListener('touchmove', handleTouchMove, { passive: true });
        (modalBody.style as any).webkitOverflowScrolling = 'touch';
        modalBody.style.transform = 'translateZ(0)';
      }
      
      return () => {
        if (!isTouchDevice) {
          modalBody.removeEventListener('wheel', handleWheel);
        } else {
          modalBody.removeEventListener('touchmove', handleTouchMove);
        }
      };
    }
  }, [isOpen, currentStep]);

  // Effet pour s'assurer que l'emoji reste bien sélectionné visuellement
  useEffect(() => {
    console.log('🎭 [CategoryCreator] State emoji actuel:', categoryEmoji);
    console.log('🎭 [CategoryCreator] Emojis disponibles:', availableEmojis);
  }, [categoryEmoji, availableEmojis]);

  const loadCategoryForEdit = async () => {
    if (!editCategory || !user) return;

    try {
      console.log('🔍 [CategoryCreator] Chargement des détails pour édition:', editCategory.id);
      
      // Obtenir le token Firebase dynamiquement
      const token = await user.getIdToken();
      
  const response = await fetch(`${API_URL}/api/categories/${editCategory.id}/details`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load category details');
      }

      const data = await response.json();
      console.log('✅ [CategoryCreator] Détails chargés:', data);
      console.log('🎭 [CategoryCreator] Emoji reçu:', data.category.emoji);
      
      // Remplir le formulaire avec les données
      setCategoryName(data.category.name);
      setCategoryDescription(data.category.description || '');
      setCategoryColor(data.category.color);
      const emojiToSet = data.category.emoji || '🎯';
      console.log('🎭 [CategoryCreator] Emoji à définir:', emojiToSet);
      setCategoryEmoji(emojiToSet);
      
      // Ajouter l'emoji de la catégorie aux emojis disponibles s'il n'y est pas
      if (emojiToSet && !baseEmojis.includes(emojiToSet)) {
        console.log('🎭 [CategoryCreator] Ajout de l\'emoji personnalisé:', emojiToSet);
        setAvailableEmojis([emojiToSet, ...baseEmojis]);
      } else {
        setAvailableEmojis([...baseEmojis]);
      }
      
      setIsPublic(data.category.is_public);
      
      // Convertir les questions pour le formulaire
      const formQuestions = data.questions.map((q: any) => ({
        id: `question_${q.id}_${Date.now()}`, // ID temporaire pour React
        language_code: q.language_code,
        question_text: q.question_text,
        option_1: q.option_1,
        option_2: q.option_2,
        option_3: q.option_3,
        option_4: q.option_4,
        correct_option: q.correct_option,
        // Convertir toute ancienne échelle (1..10) vers 1..5 au chargement
        difficulty_level: normalizeDifficulty5(q.difficulty_level),
        image_url: q.image_url || ''
      }));
      
      setQuestions(formQuestions);
      setCurrentStep(1); // Commencer à l'étape catégorie
      
    } catch (error) {
      console.error('❌ [CategoryCreator] Erreur lors du chargement:', error);
      toast({
        title: t('categoryCreator.errors.loadFailed'),
        description: 'Impossible de charger les détails de la catégorie',
        status: 'error',
        duration: 5000,
      });
    }
  };

  const resetForm = () => {
    setCategoryName('');
    setCategoryDescription('');
    setCategoryColor('#6B73FF');
    setCategoryEmoji('🎯');
    setIsPublic(true);
    setQuestions([]);
    setCurrentStep(1);
    setEditingQuestionId(null);
    setAvailableEmojis([...baseEmojis]);
  };

  const createEmptyQuestion = (): QuestionForm => ({
    id: `temp_${Date.now()}_${Math.random()}`,
    question_text: '',
    option_1: '',
    option_2: '',
    option_3: '',
    option_4: '',
    correct_option: 1,
    difficulty_level: 5,
    language_code: currentLanguage,
    image_url: '',
  });

  const addQuestion = () => {
    const newQuestion = createEmptyQuestion();
    setQuestions(prev => [...prev, newQuestion]);
    setEditingQuestionId(newQuestion.id);
  };

  const updateQuestion = (id: string, updates: Partial<QuestionForm>) => {
    setQuestions(prev =>
      prev.map(q => q.id === id ? { ...q, ...updates } : q)
    );
  };

  const deleteQuestion = (id: string) => {
    setQuestions(prev => prev.filter(q => q.id !== id));
    if (editingQuestionId === id) {
      setEditingQuestionId(null);
    }
  };

  // Fonction d'export JSON
  const exportToJSON = () => {
    const exportData = {
      category: {
        name: categoryName,
        description: categoryDescription,
        color: categoryColor,
        emoji: categoryEmoji,
        is_public: isPublic,
        language_code: currentLanguage
      },
      questions: questions.map(q => ({
        question_text: q.question_text,
        option_1: q.option_1,
        option_2: q.option_2,
        option_3: q.option_3,
        option_4: q.option_4,
        correct_option: q.correct_option,
        difficulty_level: q.difficulty_level,
        language_code: q.language_code,
        image_url: q.image_url || ''
      })),
      metadata: {
        exported_at: new Date().toISOString(),
        exported_by: 'CategoryCreator',
        version: '1.1'
      }
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `${categoryName || 'category'}_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast({
      title: 'Export réussi !',
      description: `Catégorie "${categoryName}" exportée avec ${questions.length} questions`,
      status: 'success',
      duration: 3000,
    });
  };

  // Fonction d'import JSON
  const importFromJSON = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const jsonContent = e.target?.result as string;
        const importData = JSON.parse(jsonContent);

        // Validation basique de la structure
        if (!importData.category || !importData.questions) {
          throw new Error('Structure JSON invalide. Le fichier doit contenir "category" et "questions".');
        }

        const { category, questions: importedQuestions } = importData;

        if (importMode === 'replace') {
          // Mode remplacement : tout remplacer
          setCategoryName(category.name || '');
          setCategoryDescription(category.description || '');
          setCategoryColor(category.color || '#6B73FF');
          setCategoryEmoji(category.emoji || '🎯');
          setIsPublic(category.is_public !== false); // défaut true
          setCurrentLanguage(category.language_code || 'fr');

          // Remplacer toutes les questions
          const formattedQuestions = importedQuestions.map((q: any, index: number) => ({
            id: `imported_${Date.now()}_${index}`,
            question_text: q.question_text || '',
            option_1: q.option_1 || '',
            option_2: q.option_2 || '',
            option_3: q.option_3 || '',
            option_4: q.option_4 || '',
            correct_option: q.correct_option || 1,
            // Normaliser l'ancienne échelle potentielle
            difficulty_level: normalizeDifficulty5(q.difficulty_level || 5),
            language_code: q.language_code || currentLanguage,
            image_url: q.image_url || q.url_image || '' // Support des deux formats
          }));
          setQuestions(formattedQuestions);

          toast({
            title: 'Import réussi !',
            description: `Catégorie remplacée avec ${formattedQuestions.length} questions`,
            status: 'success',
            duration: 3000,
          });
        } else {
          // Mode ajout : ajouter seulement les questions
          const formattedQuestions = importedQuestions.map((q: any, index: number) => ({
            id: `imported_${Date.now()}_${index}`,
            question_text: q.question_text || '',
            option_1: q.option_1 || '',
            option_2: q.option_2 || '',
            option_3: q.option_3 || '',
            option_4: q.option_4 || '',
            correct_option: q.correct_option || 1,
            difficulty_level: normalizeDifficulty5(q.difficulty_level || 5),
            language_code: q.language_code || currentLanguage,
            image_url: q.image_url || q.url_image || '' // Support des deux formats
          }));

          setQuestions(prev => [...prev, ...formattedQuestions]);

          toast({
            title: 'Questions ajoutées !',
            description: `${formattedQuestions.length} questions ajoutées à la catégorie`,
            status: 'success',
            duration: 3000,
          });
        }

      } catch (error) {
        console.error('Erreur lors de l\'import:', error);
        toast({
          title: 'Erreur d\'import',
          description: error instanceof Error ? error.message : 'Format JSON invalide',
          status: 'error',
          duration: 5000,
        });
      }
    };

    reader.readAsText(file);
    // Reset l'input file pour permettre l'import du même fichier
    event.target.value = '';
  };

  const validateCategory = (): boolean => {
    if (!categoryName.trim()) {
      toast({
        title: t('categoryCreator.errors.nameRequired'),
        status: 'error',
        duration: 3000,
      });
      return false;
    }

    if (categoryName.length < 3) {
      toast({
        title: t('categoryCreator.errors.nameMinLength'),
        status: 'error',
        duration: 3000,
      });
      return false;
    }

    return true;
  };

  const validateQuestions = (): boolean => {
    if (questions.length < 5) {
      toast({
        title: t('categoryCreator.errors.minQuestions'),
        status: 'error',
        duration: 3000,
      });
      return false;
    }

    for (const question of questions) {
      if (!question.question_text.trim()) {
        toast({
          title: t('categoryCreator.errors.questionTextRequired'),
          status: 'error',
          duration: 3000,
        });
        return false;
      }

      if (!question.option_1.trim() || !question.option_2.trim() || 
          !question.option_3.trim() || !question.option_4.trim()) {
        toast({
          title: t('categoryCreator.errors.allOptionsRequired'),
          status: 'error',
          duration: 3000,
        });
        return false;
      }

      if (question.correct_option < 1 || question.correct_option > 4) {
        toast({
          title: t('categoryCreator.errors.invalidCorrectOption'),
          status: 'error',
          duration: 3000,
        });
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async () => {
    if (currentStep === 1) {
      if (validateCategory()) {
        setCurrentStep(2);
        if (questions.length === 0) {
          // Ajouter une première question par défaut
          addQuestion();
        }
      }
      return;
    }

    if (!validateQuestions()) return;

    setIsSubmitting(true);

  const isEditMode = !!editCategory;
  try {
      // Obtenir le token Firebase dynamiquement
      const token = await user.getIdToken();
      
      const categoryData: CreateCategoryRequest = {
        name: categoryName.trim(),
        description: categoryDescription.trim(),
        color: categoryColor,
        emoji: categoryEmoji,
        is_public: isPublic,
        // Normaliser la difficulté avant envoi pour garantir 1..5
        questions: questions.map(({ id, ...question }) => ({
          ...question,
          difficulty_level: normalizeDifficulty5((question as any).difficulty_level)
        }))
      };

      console.log('🎯 [CategoryCreator] Envoi de la catégorie:', categoryData);
      console.log('📝 [CategoryCreator] Emoji envoyé:', categoryEmoji);

      const url = isEditMode 
        ? `${API_URL}/api/categories/${editCategory.id}`
        : `${API_URL}/api/categories/create`;
      
      const method = isEditMode ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(categoryData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to ${isEditMode ? 'update' : 'create'} category`);
      }

      const result = await response.json();
      console.log(`✅ [CategoryCreator] Catégorie ${isEditMode ? 'modifiée' : 'créée'}:`, result);

      // Tracker l'événement GTM spécifique à la création/édition
      sendGTMEvent({
        event: isEditMode ? 'category_updated' : 'category_created',
        category: categoryName,
        question_count: questions.length
      });
      
      toast({
        title: isEditMode 
          ? t('categoryCreator.success.categoryUpdated')
          : t('categoryCreator.success.categoryCreated'),
        description: isEditMode
          ? t('categoryCreator.success.categoryUpdatedDesc', { 
              name: categoryName,
              count: questions.length 
            })
          : t('categoryCreator.success.categoryCreatedDesc', { 
              name: categoryName,
              count: questions.length 
            }),
        status: 'success',
        duration: 5000,
      });

  onCategoryCreated?.();
      onClose();
      resetForm();

  } catch (error) {
      console.error('❌ [CategoryCreator] Erreur:', error);
      toast({
        title: isEditMode 
          ? t('categoryCreator.errors.updateFailed')
          : t('categoryCreator.errors.createFailed'),
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepIndicator = () => (
    <HStack spacing={{ base: 2, md: 4 }} justify="center" mb={6} wrap="wrap">
      <HStack spacing={2}>
        <Box
          w={8}
          h={8}
          borderRadius="full"
          className={currentStep >= 1 ? 'category-creator-step-indicator active' : 'category-creator-step-indicator'}
          color="white"
          display="flex"
          alignItems="center"
          justifyContent="center"
          fontSize="sm"
          fontWeight="bold"
          transition="all 0.3s"
        >
          1
        </Box>
        <Text fontSize={{ base: 'xs', md: 'sm' }} className={currentStep >= 1 ? 'category-creator-text' : 'category-creator-text-muted'} fontWeight="medium">
          {t('categoryCreator.steps.categoryInfo')}
        </Text>
      </HStack>
      
      <Box w={{ base: 4, md: 8 }} h={0.5} className={currentStep >= 2 ? 'category-creator-step-indicator active' : 'category-creator-step-indicator'} borderRadius="full" />
      
      <HStack spacing={2}>
        <Box
          w={8}
          h={8}
          borderRadius="full"
          className={currentStep >= 2 ? 'category-creator-step-indicator active' : 'category-creator-step-indicator'}
          color="white"
          display="flex"
          alignItems="center"
          justifyContent="center"
          fontSize="sm"
          fontWeight="bold"
          transition="all 0.3s"
        >
          2
        </Box>
        <Text fontSize={{ base: 'xs', md: 'sm' }} className={currentStep >= 2 ? 'category-creator-text' : 'category-creator-text-muted'} fontWeight="medium">
          {t('categoryCreator.steps.questions')}
        </Text>
      </HStack>
    </HStack>
  );

  const renderCategoryForm = () => (
    <VStack spacing={6}>
      <FormControl isRequired>
        <FormLabel className="category-creator-text" fontWeight="medium">{t('categoryCreator.form.categoryName')}</FormLabel>
        <Input
          value={categoryName}
          onChange={(e) => setCategoryName(e.target.value)}
          placeholder={t('categoryCreator.form.categoryNamePlaceholder')}
          maxLength={50}
          className="category-creator-input"
          _hover={{ borderColor: 'blue.300' }}
          _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px blue.500' }}
        />
        <Text fontSize="xs" className="category-creator-text-muted" textAlign="right">
          {categoryName.length}/50
        </Text>
      </FormControl>

      <FormControl>
        <FormLabel className="category-creator-text" fontWeight="medium">{t('categoryCreator.form.description')}</FormLabel>
        <Textarea
          value={categoryDescription}
          onChange={(e) => setCategoryDescription(e.target.value)}
          placeholder={t('categoryCreator.form.descriptionPlaceholder')}
          maxLength={200}
          rows={3}
          className="category-creator-textarea"
          _hover={{ borderColor: 'blue.300' }}
          _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px blue.500' }}
          resize="vertical"
        />
        <Text fontSize="xs" className="category-creator-text-muted" textAlign="right">
          {categoryDescription.length}/200
        </Text>
      </FormControl>

      <FormControl>
        <FormLabel className="category-creator-text" fontWeight="medium">{t('categoryCreator.form.color')}</FormLabel>
        <Box>
          <HStack spacing={2} wrap="wrap" justify="start">
            {predefinedColors.map((color) => (
              <Box
                key={color}
                w={{ base: 6, md: 8 }}
                h={{ base: 6, md: 8 }}
                bg={color}
                borderRadius="md"
                cursor="pointer"
                border={categoryColor === color ? '3px solid' : '2px solid'}
                borderColor={categoryColor === color ? 'blue.500' : 'gray.300'}
                onClick={() => setCategoryColor(color)}
                transition="all 0.2s"
                _hover={{ 
                  transform: 'scale(1.1)',
                  shadow: 'md'
                }}
              />
            ))}
            <Input
              type="color"
              value={categoryColor}
              onChange={(e) => setCategoryColor(e.target.value)}
              w={{ base: 6, md: 8 }}
              h={{ base: 6, md: 8 }}
              p={0}
              border="2px solid"
              borderColor="gray.300"
              borderRadius="md"
              cursor="pointer"
              _hover={{ shadow: 'md' }}
            />
          </HStack>
          <Text fontSize="xs" className="category-creator-text-muted" mt={2}>
            {t('categoryCreator.form.colorHint', { default: 'Cliquez pour sélectionner une couleur personnalisée' })}
          </Text>
        </Box>
      </FormControl>

      <FormControl>
        <FormLabel className="category-creator-text" fontWeight="medium">Emoji de la catégorie</FormLabel>
        <Box>
          <HStack spacing={2} wrap="wrap" justify="start" mb={3}>
            {availableEmojis.map((emoji, index) => {
              const isOriginalEmoji = editCategory && emoji === (editCategory.emoji || '🎯') && !baseEmojis.includes(emoji);
              return (
                <Box
                  key={emoji}
                  w={{ base: 8, md: 10 }}
                  h={{ base: 8, md: 10 }}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  fontSize={{ base: 'lg', md: 'xl' }}
                  borderRadius="md"
                  cursor="pointer"
                  border={categoryEmoji === emoji ? '3px solid' : '2px solid'}
                  borderColor={categoryEmoji === emoji ? 'blue.500' : 'gray.300'}
                  className={categoryEmoji === emoji ? 'category-creator-emoji-selected' : 'category-creator-emoji'}
                  onClick={() => setCategoryEmoji(emoji)}
                  transition="all 0.2s"
                  position="relative"
                  _hover={{ 
                    transform: 'scale(1.1)',
                    className: 'category-creator-emoji-hover'
                  }}
                >
                  {emoji}
                  {isOriginalEmoji && (
                    <Box
                      position="absolute"
                      top="-1"
                      right="-1"
                      w="3"
                      h="3"
                      bg="green.500"
                      borderRadius="full"
                      border="1px solid white"
                      title="Emoji original de la catégorie"
                    />
                  )}
                </Box>
              );
            })}
          </HStack>
          <HStack spacing={3}>
            <Text fontSize="sm" className="category-creator-text-muted">Emoji sélectionné :</Text>
            <Box
              fontSize="2xl"
              p={2}
              borderRadius="md"
              border="2px solid"
              borderColor="gray.300"
              className="category-creator-card"
            >
              {categoryEmoji}
            </Box>
            <Input
              value={categoryEmoji}
              onChange={(e) => setCategoryEmoji(e.target.value.slice(0, 2))}
              placeholder="🎯"
              size="sm"
              maxW="80px"
              textAlign="center"
              fontSize="lg"
              className="category-creator-input"
            />
          </HStack>
          <Text fontSize="xs" className="category-creator-text-muted" mt={2}>
            Choisissez un emoji prédéfini ou tapez le vôtre dans le champ
          </Text>
          {editCategory && categoryEmoji && !baseEmojis.includes(categoryEmoji) && (
            <Text fontSize="xs" color="green.600" mt={1} fontWeight="medium">
              ✨ Emoji personnalisé de la catégorie "{editCategory.name}" conservé
            </Text>
          )}
        </Box>
      </FormControl>

      <FormControl>
        <HStack justify="space-between" align="center">
          <VStack align="start" spacing={1}>
            <FormLabel mb={0} className="category-creator-text" fontWeight="medium">{t('categoryCreator.form.makePublic')}</FormLabel>
            <Text fontSize="sm" className="category-creator-text-muted">
              {isPublic 
                ? t('categoryCreator.form.publicDescription')
                : t('categoryCreator.form.privateDescription')
              }
            </Text>
          </VStack>
          <Switch
            isChecked={isPublic}
            onChange={(e) => setIsPublic(e.target.checked)}
            colorScheme="blue"
            size="lg"
          />
        </HStack>
      </FormControl>

      {/* Section Import/Export */}
      <Box className="category-creator-card" p={4} borderRadius="md">
        <VStack spacing={4} align="stretch">
          <Text fontSize="md" fontWeight="bold" className="category-creator-text">
            📁 Import / Export JSON
          </Text>
          
          {/* Export */}
          <Box>
            <Text fontSize="sm" className="category-creator-text-muted" mb={2}>
              Exporter votre catégorie et questions au format JSON :
            </Text>
            <Button
              onClick={exportToJSON}
              colorScheme="green"
              variant="outline"
              size="sm"
              isDisabled={!categoryName.trim() && questions.length === 0}
              leftIcon={<span>📤</span>}
            >
              Exporter JSON
            </Button>
          </Box>

          <Divider />

          {/* Import Mode Selection */}
          <Box>
            <Text fontSize="sm" className="category-creator-text-muted" mb={2}>
              Mode d'importation :
            </Text>
            <HStack spacing={4}>
              <Button
                size="sm"
                variant={importMode === 'add' ? 'solid' : 'outline'}
                colorScheme="blue"
                onClick={() => setImportMode('add')}
              >
                ➕ Ajouter
              </Button>
              <Button
                size="sm"
                variant={importMode === 'replace' ? 'solid' : 'outline'}
                colorScheme="orange"
                onClick={() => setImportMode('replace')}
              >
                🔄 Remplacer
              </Button>
            </HStack>
            <Text fontSize="xs" className="category-creator-text-muted" mt={1}>
              {importMode === 'add' 
                ? 'Ajouter les questions du fichier à la catégorie actuelle'
                : 'Remplacer complètement la catégorie et toutes les questions'
              }
            </Text>
          </Box>

          {/* Import */}
          <Box>
            <HStack justify="space-between" align="center" mb={2}>
              <Text fontSize="sm" className="category-creator-text-muted">
                Importer depuis un fichier JSON :
              </Text>
              <Button
                as="a"
                href="/example-category.json"
                download="exemple-categorie.json"
                size="xs"
                variant="ghost"
                colorScheme="blue"
                leftIcon={<span>📋</span>}
              >
                Télécharger exemple
              </Button>
            </HStack>
            <Input
              type="file"
              accept=".json"
              onChange={importFromJSON}
              size="sm"
              className="category-creator-input"
              cursor="pointer"
              _hover={{ borderColor: 'blue.300' }}
            />
            <Text fontSize="xs" className="category-creator-text-muted" mt={1}>
              Sélectionnez un fichier JSON contenant une catégorie et ses questions
            </Text>
          </Box>
        </VStack>
      </Box>
    </VStack>
  );

  const renderQuestionForm = () => (
    <VStack spacing={6}>
      <HStack justify="space-between" w="full">
        <Text fontSize="lg" fontWeight="bold">
          {t('categoryCreator.form.questions')} ({questions.length})
        </Text>
        <HStack spacing={2}>
          <Button
            onClick={exportToJSON}
            colorScheme="green"
            variant="outline"
            size="sm"
            isDisabled={!categoryName.trim() && questions.length === 0}
            leftIcon={<span>📤</span>}
          >
            Export
          </Button>
          <Button
            leftIcon={<AddIcon />}
            onClick={addQuestion}
            size="sm"
            colorScheme="blue"
          >
            {t('categoryCreator.form.addQuestion')}
          </Button>
        </HStack>
      </HStack>

      {/* Import rapide pour les questions */}
      <Box className="category-creator-card" p={3} borderRadius="md" w="full">
        <HStack justify="space-between" align="center">
          <Text fontSize="sm" className="category-creator-text" fontWeight="medium">
            📁 Import rapide
          </Text>
          <HStack spacing={2}>
            <Input
              type="file"
              accept=".json"
              onChange={importFromJSON}
              size="sm"
              className="category-creator-input"
              cursor="pointer"
              maxW="200px"
              fontSize="xs"
            />
            <Select
              value={importMode}
              onChange={(e) => setImportMode(e.target.value as 'add' | 'replace')}
              size="sm"
              maxW="100px"
            >
              <option value="add">Ajouter</option>
              <option value="replace">Remplacer</option>
            </Select>
          </HStack>
        </HStack>
      </Box>

      {/* Barre de recherche créative */}
      {questions.length > 0 && (
        <Box 
          w="full" 
          className="category-creator-card"
          p={4}
          borderRadius="lg"
          position="relative"
          overflow="hidden"
          _before={{
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(90deg, rgba(107,115,255,0.1) 0%, rgba(78,205,196,0.1) 50%, rgba(255,107,107,0.1) 100%)',
            opacity: searchQuery ? 1 : 0.5,
            transition: 'opacity 0.3s ease',
            zIndex: 0,
          }}
        >
          <VStack spacing={3} position="relative" zIndex={1}>
            <HStack w="full" spacing={2}>
              <Box 
                fontSize="2xl"
                animation={searchQuery ? 'pulse 2s ease-in-out infinite' : 'none'}
                sx={{
                  '@keyframes pulse': {
                    '0%, 100%': { transform: 'scale(1)' },
                    '50%': { transform: 'scale(1.2)' }
                  }
                }}
              >
                🔍
              </Box>
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher dans les questions ou réponses..."
                size="md"
                variant="filled"
                bg="white"
                _dark={{ bg: 'gray.700' }}
                _hover={{ bg: 'gray.50', _dark: { bg: 'gray.600' } }}
                _focus={{ 
                  bg: 'white',
                  _dark: { bg: 'gray.700' },
                  borderColor: 'blue.500',
                  boxShadow: '0 0 0 1px #6B73FF, 0 0 20px rgba(107,115,255,0.4)'
                }}
                fontWeight="medium"
                transition="all 0.3s ease"
              />
              {searchQuery && (
                <IconButton
                  aria-label="Effacer recherche"
                  icon={<span>✖️</span>}
                  size="md"
                  colorScheme="red"
                  variant="ghost"
                  onClick={() => setSearchQuery('')}
                  transition="all 0.2s"
                  _hover={{ transform: 'rotate(90deg)' }}
                />
              )}
            </HStack>
            
            {searchQuery && (
              <HStack w="full" justify="space-between" fontSize="xs" className="category-creator-text-muted">
                <Text>
                  {filteredQuestions.length} question{filteredQuestions.length > 1 ? 's' : ''} trouvée{filteredQuestions.length > 1 ? 's' : ''}
                </Text>
                <HStack spacing={1}>
                  <Box w={2} h={2} borderRadius="full" bg="green.400" animation="blink 1.5s ease-in-out infinite" 
                    sx={{
                      '@keyframes blink': {
                        '0%, 100%': { opacity: 1 },
                        '50%': { opacity: 0.3 }
                      }
                    }}
                  />
                  <Text>Recherche active</Text>
                </HStack>
              </HStack>
            )}
          </VStack>
        </Box>
      )}

      {questions.length > 0 && (
        <Box 
          w="full" 
          maxH="400px" 
          overflowY="auto"
          css={{
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#888',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#555',
            }
          }}
        >
          {filteredQuestions.length === 0 && searchQuery ? (
            <Box 
              p={8} 
              textAlign="center"
              className="category-creator-card"
              borderRadius="lg"
              position="relative"
              overflow="hidden"
            >
              <VStack spacing={3}>
                <Box fontSize="4xl" animation="float 3s ease-in-out infinite"
                  sx={{
                    '@keyframes float': {
                      '0%, 100%': { transform: 'translateY(0px)' },
                      '50%': { transform: 'translateY(-10px)' }
                    }
                  }}
                >
                  🔍
                </Box>
                <Text fontSize="lg" fontWeight="bold" className="category-creator-text">
                  Aucune question trouvée
                </Text>
                <Text fontSize="sm" className="category-creator-text-muted">
                  Essayez avec d'autres mots-clés ou{' '}
                  <Text as="span" color="blue.500" cursor="pointer" onClick={() => setSearchQuery('')} textDecoration="underline">
                    effacez la recherche
                  </Text>
                </Text>
              </VStack>
            </Box>
          ) : (
          <Accordion allowToggle index={editingQuestionId ? filteredQuestions.findIndex(q => q.id === editingQuestionId) : -1}>
            {filteredQuestions.map((question, index) => {
              // Calculer l'index réel dans la liste complète
              const realIndex = questions.findIndex(q => q.id === question.id);
              return (
              <AccordionItem key={question.id}>
                <AccordionButton
                  onClick={() => setEditingQuestionId(
                    editingQuestionId === question.id ? null : question.id
                  )}
                >
                  <Box flex="1" textAlign="left">
                    <HStack>
                      <Badge colorScheme="blue">Q{realIndex + 1}</Badge>
                      {question.image_url && <span title="Cette question a une image">🖼️</span>}
                      <Text isTruncated maxW="300px">
                        {searchQuery ? highlightText(question.question_text || t('categoryCreator.form.emptyQuestion'), searchQuery) : (question.question_text || t('categoryCreator.form.emptyQuestion'))}
                      </Text>
                      {searchQuery && (
                        <Badge colorScheme="green" fontSize="xs">✨</Badge>
                      )}
                    </HStack>
                  </Box>
                  <HStack spacing={2}>
                    <IconButton
                      aria-label="Delete question"
                      icon={<DeleteIcon />}
                      size="sm"
                      colorScheme="red"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteQuestion(question.id);
                      }}
                    />
                    <AccordionIcon />
                  </HStack>
                </AccordionButton>
                <AccordionPanel pb={4}>
                  <VStack spacing={4}>
                    <FormControl isRequired>
                      <FormLabel fontSize="sm">{t('categoryCreator.form.questionText')}</FormLabel>
                      <Textarea
                        value={question.question_text}
                        onChange={(e) => updateQuestion(question.id, { question_text: e.target.value })}
                        placeholder={t('categoryCreator.form.questionTextPlaceholder')}
                        size="sm"
                        rows={2}
                      />
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel fontSize="sm">{t('categoryCreator.form.options')}</FormLabel>
                      <VStack spacing={2}>
                        {[1, 2, 3, 4].map((optionNum) => (
                          <HStack key={optionNum} w="full">
                            <Box
                              w={6}
                              h={6}
                              borderRadius="full"
                              bg={question.correct_option === optionNum ? 'green.500' : 'gray.300'}
                              color="white"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              fontSize="xs"
                              fontWeight="bold"
                              cursor="pointer"
                              onClick={() => updateQuestion(question.id, { correct_option: optionNum })}
                            >
                              {optionNum}
                            </Box>
                            <Input
                              value={question[`option_${optionNum}` as keyof QuestionForm] as string}
                              onChange={(e) => updateQuestion(question.id, { 
                                [`option_${optionNum}`]: e.target.value 
                              })}
                              placeholder={t('categoryCreator.form.optionPlaceholder', { number: optionNum })}
                              size="sm"
                            />
                          </HStack>
                        ))}
                      </VStack>
                      <Text fontSize="xs" color="gray.500" mt={2}>
                        {t('categoryCreator.form.clickToSelectCorrect')}
                      </Text>
                    </FormControl>

                    <HStack w="full" spacing={4}>
                      <FormControl>
                        <FormLabel fontSize="sm">{t('categoryCreator.form.difficulty')}</FormLabel>
                        <Select
                          value={normalizeDifficulty5(question.difficulty_level)}
                          onChange={(e) => updateQuestion(question.id, { difficulty_level: normalizeDifficulty5(Number(e.target.value)) })}
                          size="sm"
                        >
                          <option value={1}>Très facile</option>
                          <option value={2}>Facile</option>
                          <option value={3}>Moyen</option>
                          <option value={4}>Difficile</option>
                          <option value={5}>Très difficile</option>
                        </Select>
                      </FormControl>

                      <FormControl>
                        <FormLabel fontSize="sm">{t('categoryCreator.form.language')}</FormLabel>
                        <Select
                          value={question.language_code}
                          onChange={(e) => updateQuestion(question.id, { language_code: e.target.value })}
                          size="sm"
                        >
                          <option value="fr">Français</option>
                          <option value="en">English</option>
                          <option value="es">Español</option>
                          <option value="pt">Português</option>
                        </Select>
                      </FormControl>
                    </HStack>

                    {/* Image URL Field */}
                    <FormControl>
                      <FormLabel fontSize="sm">
                        🖼️ {t('categoryCreator.form.imageUrl', 'URL de l\'image (optionnel)')}
                      </FormLabel>
                      <Input
                        value={question.image_url || ''}
                        onChange={(e) => updateQuestion(question.id, { image_url: e.target.value })}
                        placeholder="https://example.com/image.png"
                        size="sm"
                      />
                      {question.image_url && (
                        <Box mt={2} textAlign="center">
                          <img 
                            src={question.image_url} 
                            alt="Aperçu" 
                            style={{ 
                              maxWidth: '200px', 
                              maxHeight: '120px', 
                              borderRadius: '8px',
                              border: '2px solid rgba(255,255,255,0.2)',
                              objectFit: 'contain'
                            }}
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        </Box>
                      )}
                    </FormControl>
                  </VStack>
                </AccordionPanel>
              </AccordionItem>
            );
            })}
          </Accordion>
          )}
        </Box>
      )}

      {questions.length < 5 && (
        <Box
          p={4}
          border="2px dashed"
          borderColor="gray.300"
          borderRadius="md"
          textAlign="center"
          w="full"
        >
          <Text color="gray.500" fontSize="sm">
            {t('categoryCreator.form.minQuestionsWarning', { current: questions.length, min: 5 })}
          </Text>
        </Box>
      )}
    </VStack>
  );

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      size={{ base: 'full', md: '6xl' }}
      closeOnOverlayClick={false}
      scrollBehavior="inside"
    >
      <MotionBox
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
      >
        <ModalOverlay 
          bg="blackAlpha.600"
        />
        <ModalContent 
          maxH={{ base: '100vh', md: '90vh' }}
          className="category-creator-modal"
          shadow="2xl"
          m={{ base: 0, md: 4 }}
        >
          <ModalHeader 
            className="category-creator-header"
            borderTopRadius="md"
          >
            <VStack spacing={3} align="start">
              <Text fontSize={{ base: 'lg', md: 'xl' }} fontWeight="bold" className="category-creator-text">
                {editCategory 
                  ? t('categoryCreator.editTitle')
                  : t('categoryCreator.title')
                }
              </Text>
              {renderStepIndicator()}
            </VStack>
          </ModalHeader>
          <ModalCloseButton className="category-creator-text" />
          
          <ModalBody 
            ref={modalBodyRef}
            className="category-creator-body category-creator-scrollbar category-modal-body-scrollable"
            overflowY="auto" 
            p={{ base: 4, md: 6 }}
            tabIndex={-1}
            style={{ 
              outline: 'none',
              WebkitOverflowScrolling: 'touch'
            }}
            onWheel={(e) => {
              // Forcer la gestion du scroll de la souris
              e.stopPropagation();
            }}
            onTouchStart={(e) => {
              // Améliorer le touch scroll sur mobile
              e.stopPropagation();
            }}
            onTouchMove={(e) => {
              // Permettre le scroll tactile
              e.stopPropagation();
            }}
          >
            {currentStep === 1 ? renderCategoryForm() : renderQuestionForm()}
          </ModalBody>

          <ModalFooter 
            className="category-creator-footer"
            borderBottomRadius="md"
            p={{ base: 4, md: 6 }}
          >
            <HStack spacing={3} w="full" justify="flex-end" wrap="wrap">
              {currentStep === 2 && (
                <Button
                  onClick={() => setCurrentStep(1)}
                  variant="ghost"
                  colorScheme="gray"
                  size={{ base: 'sm', md: 'md' }}
                >
                  {t('categoryCreator.buttons.back')}
                </Button>
              )}
              
              <Button
                onClick={onClose}
                variant="ghost"
                colorScheme="gray"
                size={{ base: 'sm', md: 'md' }}
              >
                {t('categoryCreator.buttons.cancel')}
              </Button>
              
              <Button
                colorScheme="blue"
                onClick={handleSubmit}
                isLoading={isSubmitting}
                loadingText={currentStep === 1 ? t('categoryCreator.buttons.continuing') : t('categoryCreator.buttons.creating')}
                size={{ base: 'sm', md: 'md' }}
                minW={{ base: '120px', md: '140px' }}
              >
                {currentStep === 1 
                  ? t('categoryCreator.buttons.continue')
                  : editCategory
                    ? t('categoryCreator.buttons.updateCategory')
                    : t('categoryCreator.buttons.createCategory')
                }
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </MotionBox>
    </Modal>
  );
};

export default CategoryCreator;
